import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { BrowserApiService } from '../browser-api/browser-api.service';

// Store domain globally for SSR (set by server.ts middleware)
declare const global: any;

@Injectable({
  providedIn: 'root'
})
export class DomainNameService {
  private browserApiService = inject(BrowserApiService);
  private platformId = inject(PLATFORM_ID);
  private isServer = isPlatformServer(this.platformId);

  private getSSRDomain(): string {
    if (!this.isServer) {
      return '';
    }

    // Read from global variable set by server.ts
    try {
      const domain = (global as any).__ssr_domain__;
      return domain || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * http://localhost:4200:8080
   * Returns the application URL for API calls
   */
  public getApplicationUrl(): string {
    const location = this.browserApiService.getLocation();
    if (location) {
      return location.protocol + '//' + location.hostname;
    }

    // SSR: Read domain from global variable
    const ssrDomain = this.getSSRDomain();
    if (ssrDomain) {
      // Extract hostname from full URL
      try {
        const url = new URL(ssrDomain);
        return `${url.protocol}//${url.hostname}`;
      } catch (error) {
        return ssrDomain;
      }
    }

    return '';
  }

  /**
   * http://localhost:4200
   */
  public getApplicationDomainName(): string {
    const location = this.browserApiService.getLocation();
    if (location) {
      return location.protocol + '//' + location.host;
    }

    const ssrDomain = this.getSSRDomain();
    return ssrDomain || 'http://localhost:4000';
  }

  /**
   * localhost:4200
   */
  public getApplicationHost(): string {
    const location = this.browserApiService.getLocation();
    if (location) {
      return location.host || 'localhost:4000';
    }

    const ssrDomain = this.getSSRDomain();
    if (ssrDomain) {
      try {
        const url = new URL(ssrDomain);
        return url.host;
      } catch {
        return 'localhost:4000';
      }
    }
    return 'localhost:4000';
  }

  /**
   * localhost
   */
  public getApplicationHostname(): string {
    const location = this.browserApiService.getLocation();
    if (location) {
      return location.hostname || 'localhost';
    }

    const ssrDomain = this.getSSRDomain();
    if (ssrDomain) {
      try {
        const url = new URL(ssrDomain);
        return url.hostname;
      } catch {
        return 'localhost';
      }
    }
    return 'localhost';
  }
}
